import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Navigate, NavLink, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { AuthGate } from './auth/AuthGate';
import { CalendarAuthProvider } from './auth/CalendarAuthContext';

const ProjectStatusPage = lazy(() => import('./pages/ProjectStatusPage'));
const AttendancePage = lazy(() => import('./pages/AttendancePage'));
const SalesPage = lazy(() => import('./pages/SalesPage'));

/**
 * OwnerOnly — `_マスタ` シートの role 列が「オーナー」 のときだけ children を描画。
 * それ以外 (= 編集者 / 営業 / 未承認 / 未ログイン) は「閲覧権限がありません」 画面を出す。
 *
 * 5/14 ミーティングで合意: プロジェクト状況はオーナーのみ閲覧可。
 */
function OwnerOnly({ children }: { children: ReactNode }): JSX.Element {
  const { user } = useAuth();
  if (user?.role !== 'オーナー') {
    return (
      <main className="page auth-page">
        <h1>閲覧権限がありません</h1>
        <p className="empty-state">
          このページは灯文舎オーナーロールのメンバーのみが閲覧できます。
          {user?.role ? ` (現在のロール: ${user.role})` : ''}
        </p>
        <p>
          <NavLink to="/attendance">出勤管理に戻る</NavLink>
        </p>
      </main>
    );
  }
  return <>{children}</>;
}

/** 動的ナビ: オーナーロール以外は「プロジェクト状況」 リンクを非表示。 */
function NavLinks(): JSX.Element {
  const { user } = useAuth();
  const isOwner = user?.role === 'オーナー';
  return (
    <ul className="nav-links">
      {isOwner && (
        <li>
          <NavLink to="/project-status" className={({ isActive }) => (isActive ? 'is-active' : '')}>
            プロジェクト状況
          </NavLink>
        </li>
      )}
      <li>
        <NavLink to="/attendance" className={({ isActive }) => (isActive ? 'is-active' : '')}>
          出勤管理
        </NavLink>
      </li>
      <li>
        <NavLink to="/sales" className={({ isActive }) => (isActive ? 'is-active' : '')}>
          売上管理
        </NavLink>
      </li>
    </ul>
  );
}

/** デフォルトルート (`/`) のリダイレクト先: オーナー → /project-status、 それ以外 → /attendance */
function DefaultRedirect(): JSX.Element {
  const { user } = useAuth();
  const target = user?.role === 'オーナー' ? '/project-status' : '/attendance';
  return <Navigate to={target} replace />;
}

export default function App(): JSX.Element {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CalendarAuthProvider>
        <div className="app-shell">
          <nav className="global-nav" aria-label="主ナビゲーション">
            <div className="brand">
              <span className="brand-mark" aria-hidden>灯</span>
              <span className="brand-text">
                <span className="brand-title">灯文舎ダッシュボード</span>
                <span className="brand-subtitle">プロジェクト状況 / 出勤管理 / 売上管理</span>
              </span>
            </div>
            <NavLinks />
          </nav>
          <Suspense fallback={
            <div className="page">
              <span className="loading-inline" role="status" aria-live="polite">
                <span className="loading-spinner" aria-hidden="true" />
                読み込み中…
              </span>
            </div>
          }>
            <Routes>
              <Route path="/" element={<DefaultRedirect />} />
              <Route
                path="/project-status"
                element={
                  <AuthGate>
                    <OwnerOnly>
                      <ProjectStatusPage />
                    </OwnerOnly>
                  </AuthGate>
                }
              />
              <Route
                path="/attendance"
                element={
                  <AuthGate>
                    <AttendancePage />
                  </AuthGate>
                }
              />
              <Route
                path="/sales"
                element={
                  <AuthGate>
                    <SalesPage />
                  </AuthGate>
                }
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <footer className="global-footer">
            <small>灯文舎 顧客管理ダッシュボード</small>
          </footer>
        </div>
        </CalendarAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

function NotFound(): JSX.Element {
  return (
    <main className="page">
      <h1>ページが見つかりません</h1>
      <p className="empty-state">URL をご確認ください。</p>
    </main>
  );
}
